import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Share2, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Star, ShoppingCart, Truck, Shield, RotateCcw, Check, Minus, Plus,
  Package, ArrowRight, X, Calendar, User,
} from "lucide-react";

import { AppDispatch, RootState } from "../../redux/store";
import { listenToClassifiedData, ClassifiedListing } from "../../redux/classifiedsSlice";
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import { fetchClassifiedReviewsByListing } from "../../redux/classifiedRatingSlice";
import { listenToCommerceData } from "../../redux/commercePartnersSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";

import {
  electronicsAttributes,
  electronicsColorAttributes,
  electronicsSizeAttributes,
  ElectronicsAttributeGroup,
} from "../ATTRIBUTES/ElectronicsAttributes";

/* ════════════════════════════════════════════════════════════
   DESIGN TOKENS — mirrors GlamourDetails exactly
════════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#F7F9FF",
  bgMuted:    "#EEF4FF",
  black:      "#060D1F",
  blackMid:   "#0D1B35",
  blackSoft:  "#1A2D4F",
  border:     "#DCE8FF",
  borderDark: "#A8C4D8",
  muted:      "#6B8CB8",
  mutedSoft:  "#A8C4D8",
  blue:       "#0A66C2",
  blueLight:  "#5BCBFF",
  gold:       "#FEBA02",
  goldLight:  "#FFF3CC",
  red:        "#CC0000",
  grad:       "linear-gradient(135deg,#003580 0%,#0061fe 60%,#FEBA02 100%)",
  gradText:   "linear-gradient(90deg,#0A66C2,#5BCBFF)",
};

/* ── Stars renderer — identical to GlamourDetails ── */
const Stars: React.FC<{ rating: number; size?: number; count?: number }> = ({ rating, size = 13, count }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} style={{ position: "relative", display: "inline-block", width: size, height: size }}>
        <Star size={size} color={T.borderDark} fill={T.borderDark} />
        {i <= Math.floor(rating) ? (
          <span style={{ position: "absolute", inset: 0 }}><Star size={size} color={T.blue} fill={T.blue} /></span>
        ) : i - 1 < rating && rating < i ? (
          <span style={{ position: "absolute", inset: 0, overflow: "hidden", width: `${(rating - (i - 1)) * 100}%` }}>
            <Star size={size} color={T.blue} fill={T.blue} />
          </span>
        ) : null}
      </span>
    ))}
    {count !== undefined && (
      <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginLeft: 4 }}>({count}+)</span>
    )}
  </div>
);

/* ── Rating bar ── */
const RatingBar: React.FC<{ label: string; value: number; total?: number }> = ({ label, value, total }) => {
  const pct = total && total > 0 ? Math.round((value / total) * 100) : Math.round((value / 5) * 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", width: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 4, background: T.bgMuted, overflow: "hidden", borderRadius: 2 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: T.grad, transition: "width .6s", borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: T.black, fontFamily: "'DM Sans',sans-serif", width: 28, textAlign: "right" }}>
        {typeof total === "number" ? `${pct}%` : value.toFixed(1)}
      </span>
    </div>
  );
};

/* ── Date formatter ── */
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const getInitials = (name?: string) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const MetaCommerceDetails: React.FC = () => {
  const { listingId: paramId } = useParams<{ listingId: string }>();
  const navigate  = useNavigate();
  const dispatch  = useDispatch<AppDispatch>();
  const { rate: fxRate, symbol } = useContext(CurrencyContext);

  const authUser      = useSelector((s: RootState) => s.auth.user);
  const userId        = authUser?.userId;
  const allUsers      = useSelector((s: RootState) => s.auth.allUsers);
  const cartItems     = useSelector((s: RootState) => s.cart.items);
  const product       = useSelector((s: RootState) =>
    s.classifieds.listings.find((l: ClassifiedListing) => l.listingId === paramId)
  );
  const reviews       = useSelector((s: RootState) => paramId ? s.classifiedRatings.reviews[paramId] ?? [] : []);
  const currentPartner = useSelector((s: RootState) => s.commercePartners.currentPartner);

  /* ── UI state ── */
  const [activeId,   setActiveId]   = useState(paramId ?? "");
  const [selColor,   setSelColor]   = useState<number | null>(null);
  const [selSize,    setSelSize]    = useState<number | null>(null);
  const [qty,        setQty]        = useState(1);
  const [imgIndex,   setImgIndex]   = useState(0);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [wished,     setWished]     = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── Load data ── */
  useEffect(() => {
    if (!activeId) return;
    dispatch(listenToClassifiedData(activeId));
    dispatch(fetchClassifiedReviewsByListing(activeId));
    setSelColor(null); setSelSize(null); setQty(1); setImgIndex(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeId, dispatch]);

  useEffect(() => {
    if (product?.brandId) dispatch(listenToCommerceData(product.brandId));
  }, [product?.brandId, dispatch]);

  /* ── Variants ── */
  const variantMode: "fashion" | "beauty" | null = useMemo(() => {
    if (!product?.variants || Object.keys(product.variants).length === 0) return null;
    const hasSizes = Object.values(product.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length > 0);
    if (hasSizes) return "fashion";
    const hasBeauty = Object.values(product.variants).some((v: any) => typeof v.imageUrl === "string");
    if (hasBeauty) return "beauty";
    return null;
  }, [product]);

  const colourIds   = variantMode ? Object.keys(product?.variants ?? {}).map(Number) : [];
  const hasVariants = colourIds.length > 0;
  const sizeIds: number[] = selColor ? (product?.variants?.[selColor]?.sizeIds ?? []) : [];
  const variantImg  = selColor ? (product?.variants?.[selColor]?.imageUrl ?? null) : null;

  /* ── Images ── */
  const photos: string[] = useMemo(() => [
    ...(variantImg ? [variantImg] : []),
    ...(product?.photos ?? []).filter(p => p !== variantImg),
  ], [product, variantImg]);

  const media = useMemo(() => [
    ...(product?.photos ?? []).map(url => ({ type: "image" as const, url })),
    ...(product?.videoUrl ? [{ type: "video" as const, url: product.videoUrl }] : []),
  ], [product]);

  /* ── Electronics specs ── */
  const electronicsSpecs = useMemo(() => {
    if (!product?.attributes) return [];
    return Object.entries(product.attributes).map(([gid, vid]) => {
      const group = electronicsAttributes.find(g => g.id === Number(gid));
      if (!group) return null;
      if (Array.isArray(vid)) {
        const values = group.values.filter(v => (vid as number[]).includes(v.id));
        return { group, values };
      }
      if (typeof vid === "number") {
        const value = group.values.find(v => v.id === vid);
        if (!value) return null;
        return { group, values: [value] };
      }
      return null;
    }).filter(Boolean) as { group: ElectronicsAttributeGroup; values: { id: number; name: string }[] }[];
  }, [product]);

  /* ── Pricing ── */
  const fx      = fxRate ?? 1;
  const sym     = symbol ?? "$";
  const price   = product ? `${sym}${((product.rate ?? 0) * fx).toFixed(2)}` : "";
  const origPrice = (product as any)?.compareAtRate
    ? `${sym}${((product as any).compareAtRate * fx).toFixed(2)}`
    : null;
  const discount = (product as any)?.compareAtRate && (product as any).compareAtRate > (product?.rate ?? 0)
    ? Math.round((((product as any).compareAtRate - product!.rate!) / (product as any).compareAtRate) * 100)
    : null;

  /* ── Cart ── */
  const cartQty  = cartItems.filter(i => i.listingId === activeId).reduce((s, i) => s + i.qty, 0);
  const totalQty = cartItems.reduce((s, i) => s + i.qty, 0);
  const totalPrice = cartItems.reduce((s, i) => s + i.qty * i.price, 0);

  const canBuy = !hasVariants ||
    (variantMode === "fashion" && selColor !== null && (sizeIds.length === 0 || selSize !== null)) ||
    (variantMode === "beauty" && selColor !== null);

  const hostUser = useMemo(() => allUsers.find(u => u.userId === product?.hostId) ?? null, [allUsers, product?.hostId]);
  const brand = currentPartner;

  const handleAddToCart = () => {
    if (!product || !canBuy) return;
    dispatch(addToCart({
      listingId: product.listingId,
      name:      product.name ?? "",
      price:     product.rate ?? 0,
      qty,
      photo:     variantImg ?? photos[0],
      colorId:   selColor ?? undefined,
      sizeId:    selSize  ?? undefined,
      hostId:    product.hostId,
      brandId:   product.brandId,
    }));
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate(`/metacheckout/${userId}`, { state: { userId, totalQty, totalPrice, items: cartItems } });
  };

  /* ── Color name lookup ── */
  const getColorName = (id: number) =>
    electronicsColorAttributes?.values?.find((v: any) => v.id === id)?.name ?? String(id);
  const getSizeName = (id: number) =>
    electronicsSizeAttributes?.values?.find((v: any) => v.id === id)?.name ?? String(id);

  /* ════════════════════════════════════════════════════════════
     LOADING
  ════════════════════════════════════════════════════════════ */
  if (!product) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 20, height: 20, border: `2px solid ${T.border}`, borderTopColor: T.blue, borderRadius: "50%", animation: "metaSpin 1s linear infinite" }} />
      <style>{`@keyframes metaSpin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     REVIEWS SECTION
  ════════════════════════════════════════════════════════════ */
  const ReviewsSection: React.FC<{ mob?: boolean }> = ({ mob }) => {
    const [showAll, setShowAll] = useState(false);
    const displayed = showAll ? reviews : reviews.slice(0, 3);
    const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

    return (
      <section style={{ padding: mob ? "0 16px 24px" : "0 0 32px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: mob ? 18 : 22, fontWeight: 500, color: T.black, letterSpacing: "0.01em" }}>
            Customer Reviews {reviews.length > 0 ? `(${reviews.length})` : ""}
          </h3>
          {reviews.length > 3 && (
            <button onClick={() => setShowAll(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
              {showAll ? "Show less" : "View all"} <ArrowRight size={11} />
            </button>
          )}
        </div>

        {reviews.length > 0 && (
          <div style={{ background: T.bgSoft, border: `1px solid ${T.border}`, padding: "16px 18px", marginBottom: 16, borderRadius: 12 }}>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {/* Big score */}
              <div style={{ textAlign: "center", minWidth: 80 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 44, fontWeight: 400, color: T.black, lineHeight: 1 }}>
                  {avg.toFixed(1)}
                </div>
                <Stars rating={avg} size={14} />
                <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginTop: 4 }}>
                  {reviews.length} reviews
                </div>
              </div>
              {/* Breakdown by star */}
              <div style={{ flex: 1, minWidth: 160 }}>
                {[5, 4, 3, 2, 1].map(star => {
                  const cnt = reviews.filter(r => Math.round(r.rating) === star).length;
                  return (
                    <RatingBar key={star} label={`${star} star${star > 1 ? "s" : ""}`} value={cnt} total={reviews.length} />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {displayed.map((rev, i) => {
          const user = allUsers.find(u => u.userId === rev.guestId);
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              style={{ paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.bgMuted, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
                  {user?.profilePicture
                    ? <img src={user.profilePicture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.blue }}>
                        {(user?.firstName?.[0] ?? "U").toUpperCase()}
                      </div>
                  }
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.black, fontFamily: "'DM Sans',sans-serif" }}>
                    {user?.firstName ?? "Verified Buyer"}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>
                    {fmtDate(rev.dateCreated)}
                  </div>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <Stars rating={rev.rating} size={12} />
                </div>
              </div>
              {rev.title && (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.black, fontFamily: "'DM Sans',sans-serif", marginBottom: 4 }}>{rev.title}</div>
              )}
              <div style={{ fontSize: 13, color: T.blackSoft, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.7 }}>{rev.reviewText}</div>
              {Array.isArray(rev.images) && rev.images.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {rev.images.map((img: string, j: number) => (
                    <img key={j} src={img} alt="" style={{ width: 64, height: 64, objectFit: "cover", border: `1px solid ${T.border}`, borderRadius: 8 }} />
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}

        {reviews.length === 0 && (
          <div style={{ padding: "24px 0", textAlign: "center" }}>
            <Star size={28} color={T.mutedSoft} style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>No reviews yet — be the first</p>
          </div>
        )}
      </section>
    );
  };

  /* ════════════════════════════════════════════════════════════
     BRAND & SELLER SECTION
  ════════════════════════════════════════════════════════════ */
  const BrandSellerSection: React.FC<{ mob?: boolean }> = ({ mob }) => (
    <section style={{ padding: mob ? "0 16px 24px" : "0 0 32px" }}>
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: mob ? 18 : 22, fontWeight: 500, color: T.black }}>
          About the Seller
        </h3>
      </div>

      <div style={{ display: "flex", gap: mob ? 0 : 32, flexDirection: mob ? "column" : "row", alignItems: mob ? "stretch" : "flex-start" }}>

        {/* Brand card */}
        {brand && (
          <div style={{ flex: 1, background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px 18px", marginBottom: mob ? 12 : 0 }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: T.bgMuted, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
                {brand.image
                  ? <img src={brand.image} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: T.blue }}>
                      {getInitials(brand.name)}
                    </div>
                }
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.black, fontFamily: "'DM Sans',sans-serif", marginBottom: 2 }}>{brand.name}</div>
                <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>OFFICIAL BRAND</div>
              </div>
            </div>
            {brand.joinDate && (
              <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
                <Calendar size={11} /> Partnered {fmtDateTime(brand.joinDate)}
              </div>
            )}
          </div>
        )}

        {/* Host / seller card */}
        {hostUser && (
          <div style={{ flex: 1, background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px 18px" }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: T.bgMuted, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
                {hostUser.profilePicture
                  ? <img src={hostUser.profilePicture} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: T.blue }}>
                      {(hostUser.firstName?.[0] ?? "U").toUpperCase()}
                    </div>
                }
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.black, fontFamily: "'DM Sans',sans-serif", marginBottom: 2 }}>
                  {hostUser.firstName}
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>SOLD BY</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", display: "flex", flexDirection: "column", gap: 3 }}>
              {hostUser.joinDate && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Calendar size={11} /> Joined {fmtDateTime(hostUser.joinDate)}
                </span>
              )}
              {hostUser.address?.country && (
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <Package size={11} /> {hostUser.address.country}
                </span>
              )}
            </div>
            <button
              onClick={() => authUser && hostUser
                ? navigate(`/messages/${hostUser.userId}`)
                : navigate("/AuthSwitch")}
              style={{
                marginTop: 14, width: "100%", padding: "10px 0",
                background: "none", border: `1.5px solid ${T.blue}`,
                color: T.blue, borderRadius: 999, cursor: "pointer",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
                fontFamily: "'DM Sans',sans-serif", transition: "all .2s",
              }}>
              MESSAGE SELLER
            </button>
          </div>
        )}
      </div>
    </section>
  );

  /* ════════════════════════════════════════════════════════════
     COLOUR + SIZE SELECTORS (shared)
  ════════════════════════════════════════════════════════════ */
  const ColourSelector = () => colourIds.length > 0 ? (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.black, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>
        COLOUR: <span style={{ fontWeight: 400, color: T.muted }}>{selColor ? getColorName(selColor) : "—"}</span>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {colourIds.map(cId => {
          const col = electronicsColorAttributes?.values?.find((v: any) => v.id === cId);
          const isSel = selColor === cId;
          return (
            <button key={cId}
              onClick={() => { setSelColor(cId); setSelSize(null); setImgIndex(0); }}
              style={{
                width: 30, height: 30, borderRadius: "50%",
                background: col?.gradient ?? col?.hexCodes?.[0] ?? T.bgMuted,
                border: isSel ? `2.5px solid ${T.blue}` : `1.5px solid ${T.border}`,
                cursor: "pointer",
                boxShadow: isSel ? `0 0 0 2px ${T.bg}, 0 0 0 4px ${T.blue}` : "none",
                transform: isSel ? "scale(1.2)" : "scale(1)",
                transition: "all .15s",
              }}
              title={col?.name ?? String(cId)}
            />
          );
        })}
      </div>
    </div>
  ) : null;

  const SizeSelector = () => sizeIds.length > 0 ? (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.black, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>
        SIZE: <span style={{ fontWeight: 400, color: T.muted }}>{selSize ? getSizeName(selSize) : "—"}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {sizeIds.map(sId => {
          const isSel = selSize === sId;
          return (
            <button key={sId} onClick={() => setSelSize(sId)}
              style={{
                padding: "8px 16px", borderRadius: 999,
                border: `1.5px solid ${isSel ? T.blue : T.border}`,
                background: isSel ? T.grad : T.bg,
                color: isSel ? "#fff" : T.black,
                fontSize: 12, fontWeight: isSel ? 700 : 400,
                cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all .15s",
              }}>
              {getSizeName(sId)}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  /* ════════════════════════════════════════════════════════════
     SPECS TABLE (electronics attributes)
  ════════════════════════════════════════════════════════════ */
  const SpecsTable = () => electronicsSpecs.length > 0 ? (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 20 }}>
      {electronicsSpecs.map(({ group, values }, i) => (
        <div key={group.id} style={{ display: "flex", padding: "10px 14px", borderTop: i > 0 ? `1px solid ${T.border}` : "none", background: i % 2 === 0 ? T.bg : T.bgSoft }}>
          <span style={{ width: 140, fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", flexShrink: 0, fontWeight: 600 }}>
            {group.name}
          </span>
          <span style={{ fontSize: 11, color: T.black, fontFamily: "'DM Sans',sans-serif" }}>
            {values.map(v => v.name).join(", ")}
          </span>
        </div>
      ))}
      {product?.description && (
        <div style={{ padding: "12px 14px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ width: 140, fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6, fontWeight: 600 }}>Description</span>
          <span style={{ fontSize: 12, color: T.black, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.8 }}>{product.description}</span>
        </div>
      )}
    </div>
  ) : product?.description ? (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 20 }}>
      <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6, fontWeight: 600 }}>Description</span>
      <span style={{ fontSize: 12, color: T.black, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.8 }}>{product.description}</span>
    </div>
  ) : null;

  /* ════════════════════════════════════════════════════════════
     MOBILE LAYOUT — mirrors GlamourDetails MobileLayout
  ════════════════════════════════════════════════════════════ */
  const MobileLayout = () => (
    <div style={{ paddingBottom: 100 }}>
      {/* Image carousel */}
      <div style={{ position: "relative", background: T.bgSoft, aspectRatio: "4/5", overflow: "hidden" }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={imgIndex}
            src={photos[imgIndex]}
            alt={product.name}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 16 }}
          />
        </AnimatePresence>

        {photos.length > 1 && (<>
          <button onClick={() => setImgIndex(i => (i - 1 + photos.length) % photos.length)}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.88)", border: "none", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setImgIndex(i => (i + 1) % photos.length)}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.88)", border: "none", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronRight size={16} />
          </button>
        </>)}

        {/* Dots */}
        <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
          {photos.map((_, i) => (
            <div key={i} onClick={() => setImgIndex(i)}
              style={{ width: i === imgIndex ? 16 : 6, height: 6, borderRadius: 3, background: i === imgIndex ? T.blue : "rgba(10,102,194,.3)", cursor: "pointer", transition: "all .2s" }} />
          ))}
        </div>

        {/* Badge + wishlist */}
        {discount && (
          <div style={{ position: "absolute", top: 12, left: 12, background: T.red, color: T.bg, fontSize: 11, fontWeight: 700, padding: "3px 8px", fontFamily: "'DM Sans',sans-serif", borderRadius: 4 }}>
            -{discount}%
          </div>
        )}
        <button onClick={() => setWished(v => !v)}
          style={{ position: "absolute", top: 12, right: 12, background: "rgba(255,255,255,.92)", border: "none", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <Heart size={16} color={wished ? T.red : T.muted} fill={wished ? T.red : "none"} />
        </button>
      </div>

      {/* Info block */}
      <div style={{ padding: "18px 16px 0" }}>
        <div style={{ fontSize: 20, fontFamily: "'Playfair Display',serif", fontWeight: 500, color: T.black, lineHeight: 1.3, marginBottom: 8 }}>
          {product.name}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 24, fontWeight: 700, color: T.black, fontFamily: "'DM Sans',sans-serif", background: T.gradText, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{price}</span>
          {origPrice && <span style={{ fontSize: 14, color: T.muted, textDecoration: "line-through", fontFamily: "'DM Sans',sans-serif" }}>{origPrice}</span>}
        </div>

        <ColourSelector />
        <SizeSelector />

        {/* Qty */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: T.black, fontFamily: "'DM Sans',sans-serif" }}>QTY</div>
          <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
            <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 36, height: 36, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.blue }}><Minus size={13} /></button>
            <div style={{ width: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
            <button onClick={() => setQty(q => q + 1)} style={{ width: 36, height: 36, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.blue }}><Plus size={13} /></button>
          </div>
        </div>
      </div>

      {/* Specs */}
      <div style={{ margin: "0 16px 16px" }}><SpecsTable /></div>

      {/* Shipping trust strip */}
      <div style={{ margin: "0 16px 16px" }}>
        {[
          { icon: <Truck size={14} />, text: "Free delivery on orders over $300", sub: "Est. 3–7 business days" },
          { icon: <RotateCcw size={14} />, text: "30-day returns", sub: "Free returns on all orders" },
          { icon: <Shield size={14} />, text: "Verified listing", sub: "Every listing is reviewed" },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ color: T.muted, marginTop: 1 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize: 12, color: T.black, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>{item.text}</div>
              <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginTop: 1 }}>{item.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <ReviewsSection mob />
      <BrandSellerSection mob />

      {/* Sticky CTA — mirrors GlamourDetails */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: T.bg, borderTop: `1px solid ${T.border}`, padding: "12px 16px", display: "flex", gap: 10, zIndex: 200 }}>
        <button onClick={handleAddToCart} disabled={!canBuy}
          style={{ flex: 1, padding: "13px 0", border: `1.5px solid ${canBuy ? T.blue : T.border}`, borderRadius: 999, background: T.bg, color: canBuy ? T.blue : T.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", fontFamily: "'DM Sans',sans-serif", cursor: canBuy ? "pointer" : "not-allowed" }}>
          ADD TO CART
        </button>
        <button onClick={handleBuyNow} disabled={!canBuy}
          style={{ flex: 1, padding: "13px 0", border: "none", borderRadius: 999, background: canBuy ? T.grad : T.bgMuted, color: canBuy ? "#fff" : T.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", fontFamily: "'DM Sans',sans-serif", cursor: canBuy ? "pointer" : "not-allowed" }}>
          BUY NOW
        </button>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     DESKTOP LAYOUT — mirrors GlamourDetails DesktopLayout
  ════════════════════════════════════════════════════════════ */
  const DesktopLayout = () => (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 40px 60px" }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 20, fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>
        <span style={{ cursor: "pointer" }} onClick={() => navigate(`/MetaCommerce/${userId}`)}>Home</span>
        <ChevronRight size={11} />
        <span style={{ cursor: "pointer" }} onClick={() => navigate(`/MetaCommerce/${userId}`)}>Electronics</span>
        <ChevronRight size={11} />
        <span style={{ color: T.black }}>{product.name}</span>
      </div>

      {/* Main 2-col grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 52, marginBottom: 52 }}>

        {/* ── Left: thumbnails + main image ── */}
        <div>
          <div style={{ display: "flex", gap: 12 }}>
            {/* Thumb strip */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 72, flexShrink: 0 }}>
              {photos.map((src, i) => (
                <div key={i} onClick={() => setImgIndex(i)}
                  style={{ width: 72, height: 88, border: `1.5px solid ${i === imgIndex ? T.blue : T.border}`, overflow: "hidden", cursor: "pointer", transition: "border-color .15s", borderRadius: 8, background: T.bgSoft }}>
                  <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 6 }} />
                </div>
              ))}
            </div>

            {/* Main image */}
            <div style={{ flex: 1, position: "relative", background: T.bgSoft, overflow: "hidden", borderRadius: 16, border: `1px solid ${T.border}` }}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={imgIndex}
                  src={photos[imgIndex]}
                  alt={product.name}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: .3 }}
                  style={{ width: "100%", aspectRatio: "4/5", objectFit: "contain", display: "block", padding: 24 }}
                />
              </AnimatePresence>

              {discount && (
                <div style={{ position: "absolute", top: 14, left: 14, background: T.red, color: T.bg, fontSize: 11, fontWeight: 700, padding: "4px 10px", fontFamily: "'DM Sans',sans-serif", borderRadius: 4 }}>
                  -{discount}%
                </div>
              )}
              <button onClick={() => setWished(v => !v)}
                style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,.95)", border: "none", width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 10px rgba(10,102,194,.1)" }}>
                <Heart size={16} color={wished ? T.red : T.muted} fill={wished ? T.red : "none"} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: product info ── */}
        <div>
          {product.sku && (
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", fontFamily: "'DM Sans',sans-serif", marginBottom: 8 }}>
              SKU: {product.sku}
            </div>
          )}

          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 500, color: T.black, lineHeight: 1.2, marginBottom: 14, letterSpacing: "0.01em" }}>
            {product.name}
          </h1>

          {/* Price */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 22 }}>
            <span style={{ fontSize: 30, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", background: T.gradText, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-0.01em" }}>
              {price}
            </span>
            {origPrice && <span style={{ fontSize: 16, color: T.muted, textDecoration: "line-through", fontFamily: "'DM Sans',sans-serif" }}>{origPrice}</span>}
            {discount && <span style={{ fontSize: 12, fontWeight: 700, color: T.red, fontFamily: "'DM Sans',sans-serif" }}>-{discount}%</span>}
          </div>

          <ColourSelector />
          <SizeSelector />

          {/* Quantity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: T.black, fontFamily: "'DM Sans',sans-serif" }}>QTY</span>
            <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 40, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.blue }}><Minus size={14} /></button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(q => q + 1)} style={{ width: 38, height: 40, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.blue }}><Plus size={14} /></button>
            </div>
          </div>

          {/* CTA — mirrors GlamourDetails exactly */}
          <div style={{ display: "flex", gap: 10, marginBottom: 26 }}>
            <button onClick={handleAddToCart} disabled={!canBuy}
              style={{ flex: 1, padding: "14px 0", border: `1.5px solid ${canBuy ? T.blue : T.border}`, borderRadius: 999, background: T.bg, color: canBuy ? T.blue : T.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", fontFamily: "'DM Sans',sans-serif", cursor: canBuy ? "pointer" : "not-allowed", transition: "all .2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              <ShoppingCart size={14} /> ADD TO CART
            </button>
            <motion.button
              whileHover={canBuy ? { scale: 1.01 } as any : {}}
              onClick={handleBuyNow} disabled={!canBuy}
              style={{ flex: 1, padding: "14px 0", border: "none", borderRadius: 999, background: canBuy ? T.grad : T.bgMuted, color: canBuy ? "#fff" : T.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", fontFamily: "'DM Sans',sans-serif", cursor: canBuy ? "pointer" : "not-allowed", transition: "all .2s", boxShadow: canBuy ? "0 8px 28px rgba(10,102,194,.25)" : "none" }}>
              BUY NOW
            </motion.button>
          </div>

          {/* Trust strip */}
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 22 }}>
            {[
              { icon: <Truck size={14} />, title: "Free delivery over $300", sub: "Estimated 3–7 business days" },
              { icon: <RotateCcw size={14} />, title: "30-day free returns", sub: "Hassle-free return policy" },
              { icon: <Shield size={14} />, title: "Verified listing", sub: "Every listing is reviewed" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 16px", borderTop: i > 0 ? `1px solid ${T.border}` : "none", background: i % 2 === 0 ? T.bg : T.bgSoft }}>
                <span style={{ color: T.blue, marginTop: 1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize: 12, color: T.black, fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>{item.title}</div>
                  <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginTop: 1 }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Specs */}
          <SpecsTable />
        </div>
      </div>

      {/* Reviews + Seller below grid */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 32 }}>
        <ReviewsSection />
        <BrandSellerSection />
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════
     IMAGE LIGHTBOX MODAL
  ════════════════════════════════════════════════════════════ */
  const LightboxModal = () => (
    <AnimatePresence>
      {modalIndex !== null && media[modalIndex]?.type === "image" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setModalIndex(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: "92vw", maxHeight: "92vh" }}>
            <img src={media[modalIndex].url} style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 16, objectFit: "contain" }} />
            <button onClick={() => setModalIndex(null)}
              style={{ position: "absolute", top: -46, right: 0, background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}>
              <X size={32} />
            </button>
            {modalIndex > 0 && (
              <button onClick={() => setModalIndex(i => i !== null ? i - 1 : i)}
                style={{ position: "absolute", left: -60, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}>
                <ChevronLeft size={48} />
              </button>
            )}
            {modalIndex < media.length - 1 && (
              <button onClick={() => setModalIndex(i => i !== null ? i + 1 : i)}
                style={{ position: "absolute", right: -60, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#fff", cursor: "pointer" }}>
                <ChevronRight size={48} />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ════════════════════════════════════════════════════════════
     ROOT RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { display: none; }
        @keyframes metaSpin { to { transform: rotate(360deg); } }
        button { font-family: inherit; }
      `}</style>

      {/* Back nav — same as GlamourDetails */}
      <div style={{ padding: isMobile ? "12px 16px" : "14px 40px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, background: T.bg, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 0 rgba(10,102,194,0.06)" }}>
        <button onClick={() => navigate(-1)}
          style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.06em" }}>
          <ChevronLeft size={14} /> Back
        </button>
      </div>

      {isMobile ? <MobileLayout /> : <DesktopLayout />}

      {/* Floating cart badge (mobile only) — replaces old red circle */}
      {isMobile && totalQty > 0 && (
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          onClick={() => navigate(`/metacheckout/${userId}`, { state: { userId, totalQty, totalPrice, items: cartItems } })}
          style={{
            position: "fixed", bottom: 88, right: 20,
            width: 54, height: 54, borderRadius: "50%",
            background: T.grad, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0 10px 30px rgba(10,102,194,.35)", zIndex: 150,
          }}>
          <ShoppingCart size={22} color="#fff" />
          <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {totalQty}
          </span>
        </motion.div>
      )}

      <LightboxModal />
    </div>
  );
};

export default MetaCommerceDetails;